import dayjs, { Dayjs } from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import localizedFormat from 'dayjs/plugin/localizedFormat';

dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);

/**
 * Parse a date value into a dayjs anchored to local day-start.
 * Backend `LocalDate` arrives as "YYYY-MM-DD". `new Date("2026-05-22")` parses
 * as UTC midnight, which is the prior calendar day west of UTC — causing
 * overdue/days-left to flip a day early. Forcing date-only strings to
 * `T00:00:00` keeps comparisons in the user's local calendar day.
 */
export const parseDateLocal = (date: string | Date): Dayjs => {
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return dayjs(date + 'T00:00:00').startOf('day');
  }
  return dayjs(date).startOf('day');
};

export const formatDate = (date: string | Date): string => {
  return dayjs(date).format('MMM D, YYYY');
};

export const formatDateTime = (date: string | Date): string => {
  return dayjs(date).format('MMM D, YYYY h:mm A');
};

export const formatRelativeTime = (date: string | Date): string => {
  return dayjs(date).fromNow();
};

export const formatISODate = (date: string | Date): string => {
  return dayjs(date).format('YYYY-MM-DD');
};

export const formatFullDate = (date: string | Date): string => {
  return dayjs(date).format('LLLL');
};

/** Today is NOT overdue — overdue means strictly before today. */
export const isOverdue = (deadline: string | Date): boolean => {
  return parseDateLocal(deadline).isBefore(dayjs().startOf('day'));
};

/** Whole calendar days from today to deadline. 0 = due today, negative = past. */
export const daysUntil = (deadline: string | Date): number => {
  return parseDateLocal(deadline).diff(dayjs().startOf('day'), 'day');
};

export type Urgency = 'OVERDUE' | 'CRITICAL' | 'APPROACHING' | 'ON_TRACK' | 'NONE';

export const getUrgency = (deadline?: string | Date | null): Urgency => {
  if (!deadline) return 'NONE';
  const days = daysUntil(deadline);
  if (days < 0) return 'OVERDUE';
  if (days <= 7) return 'CRITICAL';
  if (days <= 14) return 'APPROACHING';
  return 'ON_TRACK';
};
